import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Star, Trash2, ExternalLink, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, Loading, fmtN, rangeStartDate } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJEZKtZUB_44kRQpcQazaf7Sk";

const METRICS = [
  ["calls", "Calls"],
  ["direction_requests", "Directions"],
  ["website_clicks", "Website Clicks"],
  ["profile_views", "Profile Views"],
  ["new_reviews", "New Reviews"],
  ["avg_rating", "Avg Rating"],
];

const emptyForm = { month: new Date().toISOString().slice(0, 7), calls: "", direction_requests: "", website_clicks: "", profile_views: "", new_reviews: "", avg_rating: "" };

export default function GbpReviews({ headers, range, refreshKey, toast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/gbp-monthly`, headers()).then((r) => setRows(r.data)).catch(() => setRows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { month: form.month };
      METRICS.forEach(([k]) => { payload[k] = Number(form[k] || 0); });
      await axios.post(`${API}/admin/gbp-monthly`, payload, headers());
      toast({ title: "Month saved", description: `GBP stats for ${form.month} stored.` });
      setForm(emptyForm);
      load();
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail?.[0]?.msg || "Check the values and try again." });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (month) => {
    try {
      await axios.delete(`${API}/admin/gbp-monthly/${month}`, headers());
      load();
    } catch (e) {
      toast({ title: "Couldn't delete" });
    }
  };

  if (!rows) return <Loading />;

  const startMonth = rangeStartDate(range).toISOString().slice(0, 7);
  const inRangeRows = rows.filter((r) => r.month >= startMonth);
  const latest = rows[0];
  const prev = rows[1];

  return (
    <div className="mt-6 space-y-5" data-testid="gbp-tab">
      {/* Trend tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-white/5 border border-white/5">
        {METRICS.map(([key, label]) => {
          const cur = latest?.[key] ?? 0;
          const before = prev?.[key] ?? 0;
          const delta = cur - before;
          const Icon = !prev || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
          return (
            <div key={key} className="bg-[#0D0D0F] p-5" data-testid={`gbp-tile-${key}`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">{label}</div>
              <div className="mt-2 font-heading font-extrabold text-2xl text-white leading-none">{latest ? fmtN(cur) : "—"}</div>
              <div className={`mt-1.5 flex items-center gap-1 font-mono text-[10px] ${!prev || delta === 0 ? "text-neutral-600" : delta > 0 ? "text-emerald-400" : "text-[#E56A79]"}`}>
                <Icon size={11} /> {prev ? `${delta > 0 ? "+" : ""}${key === "avg_rating" ? delta.toFixed(1) : fmtN(delta)} vs ${prev.month}` : latest ? latest.month : "no data yet"}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* Months table */}
        <Card
          title="Monthly Entries"
          right={
            <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="gbp-review-link" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#E56A79] hover:text-white transition-colors">
              <Star size={12} /> Review link <ExternalLink size={11} />
            </a>
          }
          testid="gbp-table-card"
        >
          {inRangeRows.length === 0 ? (
            <p className="text-[13px] text-neutral-500" data-testid="gbp-empty">
              No GBP entries in this range yet — pull the numbers from your Google Business Profile performance report each month and add them with the form.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12px]">
                <thead>
                  <tr>
                    {["Month", ...METRICS.map(([, l]) => l), ""].map((c, i) => (
                      <th key={c || i} className="pb-2.5 pr-4 font-mono text-[9px] uppercase tracking-[0.14em] text-neutral-500 whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inRangeRows.map((r) => (
                    <tr key={r.month} className="border-t border-white/5" data-testid={`gbp-row-${r.month}`}>
                      <td className="py-2.5 pr-4 text-white font-semibold whitespace-nowrap">{r.month}</td>
                      {METRICS.map(([k]) => (
                        <td key={k} className="py-2.5 pr-4 text-neutral-300">{k === "avg_rating" ? (r[k] || 0).toFixed(1) : fmtN(r[k])}</td>
                      ))}
                      <td className="py-2.5">
                        <button onClick={() => remove(r.month)} aria-label={`Delete ${r.month}`} data-testid={`gbp-delete-${r.month}`} className="p-1 text-neutral-600 hover:text-[#9D0423] transition-colors">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add month */}
        <Card title="Add Month" testid="gbp-form-card">
          <form onSubmit={save} className="space-y-3">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-neutral-500 mb-1" htmlFor="gbp-month">Month</label>
              <input id="gbp-month" type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="gbp-month-input" className="w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none text-white text-[13px] px-3 py-2" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {METRICS.map(([key, label]) => (
                <div key={key}>
                  <label className="block font-mono text-[9px] uppercase tracking-[0.16em] text-neutral-500 mb-1" htmlFor={`gbp-${key}`}>{label}</label>
                  <input
                    id={`gbp-${key}`}
                    type="number"
                    min="0"
                    step={key === "avg_rating" ? "0.1" : "1"}
                    max={key === "avg_rating" ? "5" : undefined}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    placeholder="0"
                    data-testid={`gbp-input-${key}`}
                    className="w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none text-white text-[13px] px-3 py-2"
                  />
                </div>
              ))}
            </div>
            <button type="submit" disabled={saving} data-testid="gbp-save" className="w-full bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 text-white font-mono font-semibold text-[11px] uppercase tracking-[0.14em] py-3 transition-colors">
              {saving ? "Saving…" : "Save Month"}
            </button>
            <p className="text-[10px] text-neutral-600 leading-relaxed">Numbers come from Google Business Profile → Performance. Re-saving a month overwrites it.</p>
          </form>
        </Card>
      </div>
    </div>
  );
}
